/** Equirectangular local-km projection around a fixed geographic datum (same CRS for contour + wells). */

export type GeoOrigin = { lat: number; lng: number };
export type LocalKmPoint = { x: number; y: number };

const KM_PER_DEG_LAT = 110.574;

function kmPerDegLng(lat: number): number {
  return 111.32 * Math.cos((lat * Math.PI) / 180);
}

export function latLngToLocalKm(
  lat: number,
  lng: number,
  origin: GeoOrigin,
): LocalKmPoint {
  return {
    x: (lng - origin.lng) * kmPerDegLng(origin.lat),
    y: (lat - origin.lat) * KM_PER_DEG_LAT,
  };
}

export function localKmToLatLng(
  x: number,
  y: number,
  origin: GeoOrigin,
): { lat: number; lng: number } {
  return {
    lat: origin.lat + y / KM_PER_DEG_LAT,
    lng: origin.lng + x / kmPerDegLng(origin.lat),
  };
}

export function isValidGeoOrigin(o: GeoOrigin | null | undefined): o is GeoOrigin {
  return (
    !!o &&
    Number.isFinite(o.lat) &&
    Number.isFinite(o.lng) &&
    o.lat >= -90 &&
    o.lat <= 90 &&
    o.lng >= -180 &&
    o.lng <= 180
  );
}

/**
 * Stable datum for local-km CRS.
 * Prefer the user-saved contour_origin; else scenario pin; else a neutral default.
 */
export function resolveContourOrigin(opts: {
  saved?: GeoOrigin | null;
  scenario?: GeoOrigin | null;
}): GeoOrigin {
  if (isValidGeoOrigin(opts.saved)) return opts.saved;
  if (isValidGeoOrigin(opts.scenario)) return opts.scenario;
  return { lat: 60.0, lng: 5.0 };
}

/** Reproject a local-km point from one datum to another (geographic position preserved). */
export function reprojectLocalKm(
  pt: LocalKmPoint,
  from: GeoOrigin,
  to: GeoOrigin,
): LocalKmPoint {
  const g = localKmToLatLng(pt.x, pt.y, from);
  return latLngToLocalKm(g.lat, g.lng, to);
}
